import * as d3 from 'd3';

export function drawDiamondGrills_bad(
  svg,
  x,
  y,
  width,
  height,
  frameWidth = 10,
  horLines = 1,
  verLines = 1,
  lineColor = 'white',
  borderColor = 'black',
  borderWidth = 0.2,
  grillThickness = 2
) {
  const glassX = x;
  const glassY = y;
  const glassWidth = width;
  const glassHeight = height;

  const totalLines = Math.max(horLines, verLines);

  // Spacing between diagonal lines
  const spacingX = glassWidth / (totalLines + 1);
  const spacingY = glassHeight / (totalLines + 1);

  // Draw "/" diagonal lines
  for (let i = -totalLines; i <= totalLines; i++) {
    const offsetX = i * spacingX;

    svg.append('line')
      .attr('x1', glassX + offsetX)
      .attr('y1', glassY)
      .attr('x2', glassX + offsetX + glassHeight)
      .attr('y2', glassY + glassHeight)
      .attr('stroke', lineColor)
      .attr('stroke-width', grillThickness)
      .attr('stroke-linecap', 'square')
      .attr('stroke', lineColor)
      .attr('stroke-width', grillThickness)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }

  // Draw "\" diagonal lines
  for (let i = -totalLines; i <= totalLines; i++) {
    const offsetX = i * spacingX;

    svg.append('line')
      .attr('x1', glassX + offsetX)
      .attr('y1', glassY + glassHeight)
      .attr('x2', glassX + offsetX + glassHeight)
      .attr('y2', glassY)
      .attr('stroke', lineColor)
      .attr('stroke-width', grillThickness)
      .attr('stroke-linecap', 'square')
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }
}
export function drawDiamondGrills(
  svg,
  x,
  y,
  width,
  height,
  frameWidth = 10,
  horLines = 1,
  verLines = 1,
  lineColor = 'white',
  borderColor = 'black',
  borderWidth = 0.2,
  grillThickness = 2,
  shape = 'rect',
  shapeOrientation = 'left',
  y1 = 0,
  y2 = 0,
  innerPoints
) {
  const glassX = x;
  const glassY = y;
  const glassWidth = width;
  const glassHeight = height;

  // Total number of diagonal lines in each direction
  // horLines controls how many diamond rows, verLines controls columns
  // The diagonal count combines both to define the diamond grid density
  const diagCountDown = horLines + verLines; // lines going top-left → bottom-right (\)
  const diagCountUp = horLines + verLines;   // lines going top-right → bottom-left (/)

  // Cell dimensions based on the number of divisions
  const cellWidth = glassWidth / (verLines + 1);
  const cellHeight = glassHeight / (horLines + 1);

  // Build a clip path from innerPoints or the rectangular glass area
  const clipId = `diamondClip_${Math.random().toString(36).slice(2, 11)}`;
  let clipPointsStr;

  if (innerPoints && innerPoints.length >= 3) {
    clipPointsStr = innerPoints.map(p => p.join(',')).join(' ');
  } else {
    // Default rectangular clip
    clipPointsStr = [
      [glassX, glassY],
      [glassX + glassWidth, glassY],
      [glassX + glassWidth, glassY + glassHeight],
      [glassX, glassY + glassHeight]
    ].map(p => p.join(',')).join(' ');
  }

  svg.append('defs')
    .append('clipPath')
    .attr('id', clipId)
    .append('polygon')
    .attr('points', clipPointsStr);

  const grillGroup = svg.append('g')
    .attr('clip-path', `url(#${clipId})`);

  // We draw two sets of diagonal lines that cross each other to form diamond (rhombus) cells.
  //
  // The lines are spaced so that the intersections create a grid of diamonds:
  //   - "Down" diagonals (\) go from upper-left to lower-right
  //   - "Up" diagonals (/) go from upper-right to lower-left
  //
  // Spacing: each diagonal is offset by one cell unit.
  // We need enough diagonals to cover the entire glass area including corners.

  // Margin to extend lines beyond the glass area so they fully cover after clipping
  const margin = Math.max(glassWidth, glassHeight);

  // ============================================================
  // Down diagonals (\) — slope = cellHeight / cellWidth
  // ============================================================
  // A down-diagonal line passing through grid point (col, row) satisfies:
  //   (x - glassX) / cellWidth - (y - glassY) / cellHeight = col - row = const
  // We iterate over all integer offsets that produce lines crossing the glass area.
  const slopeDown = cellHeight / cellWidth;

  for (let k = -horLines; k <= verLines; k++) {
    // Line passes through (glassX + k * cellWidth, glassY) going at slope slopeDown
    // Parametrically: X = glassX + k * cellWidth + t, Y = glassY + t * slopeDown
    const startX = glassX + k * cellWidth - margin;
    const startY = glassY + (startX - (glassX + k * cellWidth)) * slopeDown;
    const endX = glassX + k * cellWidth + margin;
    const endY = glassY + (endX - (glassX + k * cellWidth)) * slopeDown;

    // Draw as a rotated thin rect (line with thickness)
    const dx = endX - startX;
    const dy = endY - startY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const cx = (startX + endX) / 2;
    const cy = (startY + endY) / 2;

    grillGroup.append('rect')
      .attr('x', cx - len / 2)
      .attr('y', cy - grillThickness / 2)
      .attr('width', len)
      .attr('height', grillThickness)
      .attr('transform', `rotate(${angle}, ${cx}, ${cy})`)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }

  // ============================================================
  // Up diagonals (/) — slope = -cellHeight / cellWidth
  // ============================================================
  const slopeUp = -cellHeight / cellWidth;

  for (let k = 0; k <= verLines + horLines + 1; k++) {
    // Line passes through (glassX + k * cellWidth, glassY) going at slope slopeUp
    const startX = glassX + k * cellWidth - margin;
    const startY = glassY + (startX - (glassX + k * cellWidth)) * slopeUp;
    const endX = glassX + k * cellWidth + margin;
    const endY = glassY + (endX - (glassX + k * cellWidth)) * slopeUp;

    const dx = endX - startX;
    const dy = endY - startY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const cx = (startX + endX) / 2;
    const cy = (startY + endY) / 2;

    grillGroup.append('rect')
      .attr('x', cx - len / 2)
      .attr('y', cy - grillThickness / 2)
      .attr('width', len)
      .attr('height', grillThickness)
      .attr('transform', `rotate(${angle}, ${cx}, ${cy})`)
      .attr('fill', lineColor)
      .attr('stroke', borderColor)
      .attr('stroke-width', borderWidth);
  }
}
